import { useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebitEvent } from "../types/walletDebits.types";

interface EventsRead {
  key: string;
  events: WalletDebitEvent[];
  error: string | null;
}

/**
 * A debit's timeline. `version` is read again whenever the debit changes (a send recorded, a reference
 * corrected, a file removed), so the new step shows without a reload.
 */
export function useWalletDebitEvents(
  debitId: string,
  version: string,
): {
  events: WalletDebitEvent[];
  isLoading: boolean;
  error: string | null;
} {
  const key = `${debitId}:${version}`;
  const [read, setRead] = useState<EventsRead | null>(null);

  useEffect(() => {
    let cancelled = false;
    WalletDebitsService.events(debitId)
      .then((events) => {
        if (!cancelled) setRead({ key, events, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setRead((previous) => ({
            key,
            events: previous?.events ?? [],
            error: HttpApiService.getErrorMessage(error),
          }));
      });
    return () => {
      cancelled = true;
    };
  }, [debitId, key]);

  return {
    events: read?.events ?? [],
    isLoading: read?.key !== key,
    error: read?.key === key ? read.error : null,
  };
}
